((global) => {
  const wrapIndex = (index, count) => {
    if (!Number.isFinite(count) || count <= 0) return 0;
    return ((index % count) + count) % count;
  };

  const physicalIndexForLogical = (index, count) => {
    if (!Number.isFinite(count) || count <= 0) return 0;
    const logicalIndex = wrapIndex(index, count);
    return count > 1 ? logicalIndex + 1 : logicalIndex;
  };

  const productLinkForSlide = (productUrl, slide) => {
    return slide?.dataset?.variantUrl || productUrl || '';
  };

  const normalizeProductOptionValueId = (value) => {
    const normalized = String(value ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : '';
  };

  const styleColorOptionValueIds = (style) => {
    const ids = new Set();
    const add = (value) => {
      const normalized = normalizeProductOptionValueId(value);
      if (normalized) ids.add(normalized);
    };
    add(style?.defaultColorOptionValueId);
    style?.media?.forEach((media) => add(media?.colorOptionValueId));
    style?.initialSlides?.forEach((slide) => add(slide?.dataset?.colorOptionValueId));
    return ids;
  };

  const productLinkForColor = (productUrl, colorOptionValueId, allowedColorOptionValueIds = new Set()) => {
    const sourceUrl = String(productUrl || '').trim();
    if (!sourceUrl) return '';
    const normalizedColorId = normalizeProductOptionValueId(colorOptionValueId);
    const allowedIds =
      allowedColorOptionValueIds instanceof Set
        ? allowedColorOptionValueIds
        : new Set(Array.from(allowedColorOptionValueIds || [], normalizeProductOptionValueId).filter(Boolean));
    const activeColorId = normalizedColorId && allowedIds.has(normalizedColorId) ? normalizedColorId : '';

    try {
      const parsedUrl = new global.URL(sourceUrl, 'https://scoreggia.invalid');
      parsedUrl.searchParams.delete('sg_color');
      if (activeColorId) parsedUrl.searchParams.set('sg_color', activeColorId);

      if (/^[a-z][a-z\d+.-]*:/i.test(sourceUrl)) return parsedUrl.href;
      if (sourceUrl.startsWith('//')) return `//${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      if (sourceUrl.startsWith('/')) return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      return `${parsedUrl.pathname.replace(/^\//, '')}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return '';
    }
  };

  const normalizeDiscoveryPayload = (payload, { maximumStyles = 6, maximumMedia = 8 } = {}) => {
    if (!payload || payload.version !== 1 || !Array.isArray(payload.styles)) return [];
    return payload.styles
      .slice(0, maximumStyles)
      .map((style) => ({
        id: String(style?.id || ''),
        label: String(style?.label || ''),
        title: String(style?.title || ''),
        url: String(style?.url || ''),
        defaultMediaId: String(style?.defaultMediaId || ''),
        defaultColorOptionValueId: normalizeProductOptionValueId(style?.defaultColorOptionValueId),
        media: Array.isArray(style?.media)
          ? style.media
              .slice(0, maximumMedia)
              .map((media) => ({
                id: String(media?.id || ''),
                position: Number(media?.position) || 0,
                type: String(media?.type || 'image'),
                colorOptionValueId: normalizeProductOptionValueId(media?.colorOptionValueId),
                url: String(media?.url || style?.url || ''),
                src: String(media?.src || ''),
                srcset: String(media?.srcset || ''),
                thumbnailSrc: String(media?.thumbnailSrc || ''),
                width: Number(media?.width) || 0,
                height: Number(media?.height) || 0,
                alt: String(media?.alt || style?.title || ''),
              }))
              .filter((media) => media.id && media.src)
          : [],
      }))
      .filter((style) => style.id && style.label && style.url && style.media.length);
  };

  const loopNavigationPlan = ({ previousIndex, nextIndex, direction = 0, count, animate = true }) => {
    const physicalIndex = physicalIndexForLogical(nextIndex, count);
    if (!animate || count <= 1) return { physicalIndex, correctionPhysicalIndex: null };
    if (direction > 0 && previousIndex === count - 1 && nextIndex === 0) {
      return { physicalIndex: count + 1, correctionPhysicalIndex: 1 };
    }
    if (direction < 0 && previousIndex === 0 && nextIndex === count - 1) {
      return { physicalIndex: 0, correctionPhysicalIndex: count };
    }
    return { physicalIndex, correctionPhysicalIndex: null };
  };

  const carouselGestureProfiles = Object.freeze({
    touch: Object.freeze({
      deadZone: 5,
      horizontalRatio: 1.04,
      verticalRatio: 1.2,
      maximumUndecidedDistance: 36,
      maximumUndecidedSamples: 3,
      distanceRatio: 0.065,
      minimumDistance: 22,
      maximumDistance: 36,
      velocityThreshold: 0.2,
      velocityDistance: 14,
    }),
    mouse: Object.freeze({
      deadZone: 8,
      horizontalRatio: 1.15,
      verticalRatio: 1,
      maximumUndecidedDistance: Number.POSITIVE_INFINITY,
      maximumUndecidedSamples: Number.POSITIVE_INFINITY,
      distanceRatio: 0.1,
      minimumDistance: 32,
      maximumDistance: 72,
      velocityThreshold: 0.45,
      velocityDistance: 18,
    }),
  });
  const responsiveTouchGestureProfile = Object.freeze({
    deadZone: 4,
    horizontalRatio: 1.05,
    verticalRatio: 1.1,
    maximumUndecidedDistance: 24,
    maximumUndecidedSamples: 4,
    distanceRatio: 0.045,
    minimumDistance: 14,
    maximumDistance: 24,
    velocityThreshold: 0.18,
    velocityDistance: 8,
  });
  const recentVelocityWindow = 100;
  const recentVelocityMaximumAge = 140;
  const recentVelocitySampleLimit = 12;
  const recentTrajectoryWindow = 72;

  const carouselGestureProfile = (pointerType = 'touch') =>
    pointerType === 'mouse' ? carouselGestureProfiles.mouse : carouselGestureProfiles.touch;

  const carouselDistanceThreshold = (width, pointerType = 'touch') => {
    const profile = carouselGestureProfile(pointerType);
    const viewportWidth = Math.max(width || 0, 1);
    return Math.max(
      profile.minimumDistance,
      Math.min(viewportWidth * profile.distanceRatio, profile.maximumDistance),
    );
  };

  const responsiveTouchDistanceThreshold = (width) => {
    const viewportWidth = Math.max(width || 0, 1);
    return Math.max(
      responsiveTouchGestureProfile.minimumDistance,
      Math.min(
        viewportWidth * responsiveTouchGestureProfile.distanceRatio,
        responsiveTouchGestureProfile.maximumDistance,
      ),
    );
  };

  const recordRecentPointerSample = (samples, x, time, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(time)) return Array.isArray(samples) ? samples : [];
    const nextSamples = Array.isArray(samples) ? samples.slice() : [];
    const latest = nextSamples[nextSamples.length - 1];
    if (latest?.time === time) {
      latest.x = x;
      if (Number.isFinite(y)) latest.y = y;
      return nextSamples;
    }
    const sample = { x, time };
    if (Number.isFinite(y)) sample.y = y;
    nextSamples.push(sample);
    const cutoff = time - recentVelocityWindow;
    while (nextSamples.length > 2 && nextSamples[1].time < cutoff) nextSamples.shift();
    if (nextSamples.length > recentVelocitySampleLimit) {
      nextSamples.splice(0, nextSamples.length - recentVelocitySampleLimit);
    }
    return nextSamples;
  };

  const recentPointerVelocity = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) return 0;
    const newest = samples[samples.length - 1];
    if (!Number.isFinite(newest?.time) || now < newest.time || now - newest.time > recentVelocityMaximumAge) return 0;
    const cutoff = newest.time - recentVelocityWindow;
    const oldest = samples.find((sample) => sample.time >= cutoff && sample.time < newest.time);
    if (!oldest) return 0;
    const elapsed = newest.time - oldest.time;
    if (elapsed <= 0) return 0;
    return (newest.x - oldest.x) / elapsed;
  };

  const recentPointerTrajectory = ({ samples, now }) => {
    if (!Array.isArray(samples) || samples.length < 2 || !Number.isFinite(now)) return { x: 0, y: 0 };
    const newest = samples[samples.length - 1];
    if (!Number.isFinite(newest?.time) || !Number.isFinite(newest?.y) || now < newest.time) return { x: 0, y: 0 };
    const cutoff = newest.time - recentTrajectoryWindow;
    const oldest = samples.find(
      (sample) => sample.time >= cutoff && sample.time < newest.time && Number.isFinite(sample.y),
    );
    if (!oldest) return { x: 0, y: 0 };
    return { x: newest.x - oldest.x, y: newest.y - oldest.y };
  };

  const normalizePointerTimestamp = (time, now) =>
    Number.isFinite(time) && Number.isFinite(now) && Math.abs(time - now) < 60_000 ? time : now;

  const swipeDirection = ({
    deltaX,
    deltaY,
    duration,
    width,
    zoomed = false,
    pointerType = 'touch',
    velocityX,
    horizontalIntent = false,
  }) => {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const elapsed = Math.max(duration, 1);
    const viewportWidth = Math.max(width || 0, 1);

    if (zoomed) {
      const strongDistance = Math.max(96, Math.min(viewportWidth * 0.28, 180));
      if (absoluteX <= absoluteY * 1.35 || absoluteX < strongDistance || absoluteX / elapsed < 0.55) return 0;
      return deltaX < 0 ? 1 : -1;
    }

    const profile = carouselGestureProfile(pointerType);
    const distanceThreshold = carouselDistanceThreshold(viewportWidth, pointerType);
    const measuredVelocity = Number.isFinite(velocityX) ? velocityX : deltaX / elapsed;
    const matchingVelocity =
      Math.sign(measuredVelocity) === Math.sign(deltaX) &&
      Math.abs(measuredVelocity) >= profile.velocityThreshold &&
      absoluteX >= profile.velocityDistance;

    if (!horizontalIntent && absoluteX <= absoluteY * profile.horizontalRatio) return 0;
    if (absoluteX < distanceThreshold && !matchingVelocity) return 0;
    return deltaX < 0 ? 1 : -1;
  };

  const responsiveSwipeDirection = (options) => {
    if (options.pointerType === 'mouse') return swipeDirection(options);
    const absoluteX = Math.abs(options.deltaX);
    const absoluteY = Math.abs(options.deltaY);
    const elapsed = Math.max(options.duration, 1);
    const measuredVelocity = Number.isFinite(options.velocityX)
      ? options.velocityX
      : options.deltaX / elapsed;
    const matchingVelocity =
      Math.sign(measuredVelocity) === Math.sign(options.deltaX) &&
      Math.abs(measuredVelocity) >= responsiveTouchGestureProfile.velocityThreshold &&
      absoluteX >= responsiveTouchGestureProfile.velocityDistance;
    if (!options.horizontalIntent && absoluteX <= absoluteY * responsiveTouchGestureProfile.horizontalRatio) return 0;
    if (absoluteX < responsiveTouchDistanceThreshold(options.width) && !matchingVelocity) return 0;
    return options.deltaX < 0 ? 1 : -1;
  };

  const zoomGestureOutcome = ({ deltaX, deltaY, duration, width, height }) => {
    const distance = Math.hypot(deltaX, deltaY);
    if (distance <= 6) return { type: 'click', direction: 0 };

    const direction = swipeDirection({ deltaX, deltaY, duration, width, zoomed: true });
    if (direction) return { type: 'navigate', direction };

    const elapsed = Math.max(duration, 1);
    const shortestViewportEdge = Math.max(1, Math.min(width || 0, height || 0));
    const swipeDistance = Math.max(72, Math.min(shortestViewportEdge * 0.18, 120));
    if (distance >= swipeDistance && distance / elapsed >= 0.5) return { type: 'swipe', direction: 0 };

    return { type: 'drag', direction: 0 };
  };

  const gestureIntent = ({
    deltaX,
    deltaY,
    threshold = 8,
    ratio = 1.15,
    verticalRatio = 1,
    maximumUndecidedDistance = Number.POSITIVE_INFINITY,
    undecidedSamples = 0,
    maximumUndecidedSamples = Number.POSITIVE_INFINITY,
    recentDeltaX = 0,
    recentDeltaY = 0,
  }) => {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const distance = Math.hypot(deltaX, deltaY);
    if (distance <= threshold) return 0;
    if (absoluteX >= absoluteY * ratio) return 1;
    if (absoluteY >= absoluteX * verticalRatio) return -1;
    const recentAbsoluteX = Math.abs(recentDeltaX);
    const recentAbsoluteY = Math.abs(recentDeltaY);
    if (Math.hypot(recentDeltaX, recentDeltaY) > threshold) {
      if (recentAbsoluteX >= recentAbsoluteY * ratio) return 1;
      if (recentAbsoluteY >= recentAbsoluteX * verticalRatio) return -1;
    }
    if (distance >= maximumUndecidedDistance || undecidedSamples >= maximumUndecidedSamples) return -1;
    return 0;
  };

  const carouselReleaseDuration = ({ width, deltaX, velocityX = 0, accepted = false }) => {
    const viewportWidth = Math.max(Number(width) || 0, 1);
    const draggedDistance = Math.min(Math.abs(Number(deltaX) || 0), viewportWidth);
    const progress = draggedDistance / viewportWidth;
    const speed = Math.min(Math.abs(Number(velocityX) || 0), 1.2);
    if (accepted) {
      const remainingRatio = 1 - progress;
      return Math.round(Math.max(140, Math.min(220, 130 + remainingRatio * 90 - speed * 35)));
    }
    return Math.round(Math.max(100, Math.min(170, 90 + progress * 110 - speed * 30)));
  };

  const clampScale = (value, minimum = 1, maximum = 5) => {
    const safeMinimum = Number.isFinite(minimum) ? minimum : 1;
    const safeMaximum = Number.isFinite(maximum) ? Math.max(maximum, safeMinimum) : Math.max(5, safeMinimum);
    const safeValue = Number.isFinite(value) ? value : safeMinimum;
    return Math.min(safeMaximum, Math.max(safeMinimum, safeValue));
  };

  const normalizeWheelDelta = ({
    deltaY,
    deltaMode = 0,
    lineHeight = 16,
    pageHeight = 800,
    maximumMagnitude = 240,
  }) => {
    let normalized = Number(deltaY) || 0;
    if (deltaMode === 1) normalized *= Math.max(Number(lineHeight) || 0, 1);
    else if (deltaMode === 2) normalized *= Math.max(Number(pageHeight) || 0, 1);
    const limit = Math.max(Number(maximumMagnitude) || 0, 1);
    return Math.max(-limit, Math.min(limit, normalized));
  };

  const wheelScaleFromDelta = ({
    scale,
    deltaY,
    minimumScale = 1,
    maximumScale = 5,
    sensitivity = 0.002,
  }) =>
    clampScale(
      (Number(scale) || minimumScale) * Math.exp(-(Number(deltaY) || 0) * sensitivity),
      minimumScale,
      maximumScale,
    );

  const zoomTransformAroundPoint = ({
    point,
    center,
    scale,
    nextScale,
    panX = 0,
    panY = 0,
  }) => {
    const safeScale = Math.max(Number(scale) || 0, 0.0001);
    const safeNextScale = Math.max(Number(nextScale) || 0, 0.0001);
    const centerX = Number(center?.x) || 0;
    const centerY = Number(center?.y) || 0;
    const pointX = Number(point?.x) || centerX;
    const pointY = Number(point?.y) || centerY;
    const imageX = (pointX - centerX - (Number(panX) || 0)) / safeScale;
    const imageY = (pointY - centerY - (Number(panY) || 0)) / safeScale;
    return {
      scale: safeNextScale,
      x: pointX - centerX - imageX * safeNextScale,
      y: pointY - centerY - imageY * safeNextScale,
    };
  };

  const containedImageBounds = ({
    bounds,
    naturalWidth,
    naturalHeight,
    objectFit = 'contain',
    objectPosition = '50% 50%',
  }) => {
    if (!bounds || ![bounds.left, bounds.top, bounds.width, bounds.height].every(Number.isFinite)) return null;
    const boxWidth = Math.max(bounds.width, 0);
    const boxHeight = Math.max(bounds.height, 0);
    const intrinsicWidth = Math.max(Number(naturalWidth) || 0, 0);
    const intrinsicHeight = Math.max(Number(naturalHeight) || 0, 0);
    const fit = String(objectFit || '').trim().toLowerCase();
    if (!boxWidth || !boxHeight || !intrinsicWidth || !intrinsicHeight || !['contain', 'scale-down'].includes(fit)) {
      return { left: bounds.left, top: bounds.top, width: boxWidth, height: boxHeight };
    }

    let scale = Math.min(boxWidth / intrinsicWidth, boxHeight / intrinsicHeight);
    if (fit === 'scale-down') scale = Math.min(scale, 1);
    const width = intrinsicWidth * scale;
    const height = intrinsicHeight * scale;
    const freeX = boxWidth - width;
    const freeY = boxHeight - height;
    const tokens = String(objectPosition || '50% 50%').trim().split(/\s+/).filter(Boolean);
    let horizontal = tokens[0] || '50%';
    let vertical = tokens[1] || '50%';
    if (tokens.length === 1) {
      if (['top', 'bottom'].includes(horizontal.toLowerCase())) {
        vertical = horizontal;
        horizontal = '50%';
      } else {
        vertical = '50%';
      }
    } else if (
      ['top', 'bottom'].includes(horizontal.toLowerCase()) &&
      ['left', 'right', 'center'].includes(vertical.toLowerCase())
    ) {
      [horizontal, vertical] = [vertical, horizontal];
    }
    const positionOffset = (token, freeSpace, startKeyword, endKeyword) => {
      const normalized = String(token || '').trim().toLowerCase();
      if (normalized === startKeyword) return 0;
      if (normalized === endKeyword) return freeSpace;
      if (normalized === 'center') return freeSpace / 2;
      if (normalized.endsWith('%')) {
        const percentage = Number.parseFloat(normalized);
        if (Number.isFinite(percentage)) return freeSpace * percentage / 100;
      }
      if (normalized.endsWith('px')) {
        const pixels = Number.parseFloat(normalized);
        if (Number.isFinite(pixels)) return pixels;
      }
      return freeSpace / 2;
    };
    return {
      left: bounds.left + positionOffset(horizontal, freeX, 'left', 'right'),
      top: bounds.top + positionOffset(vertical, freeY, 'top', 'bottom'),
      width,
      height,
    };
  };

  const desktopZoomReturnGeometry = ({
    scale,
    initialScale = 2,
    sourceBounds,
    viewportWidth,
    viewportHeight,
    viewportLeft = 0,
    viewportTop = 0,
    userPanX = 0,
    userPanY = 0,
  }) => {
    if (!sourceBounds) return null;
    const safeInitialScale = Math.max(Number(initialScale) || 1, 1);
    const safeScale = Math.max(Number(scale) || 1, 1);
    const normalizedScale =
      safeInitialScale <= 1 ? (safeScale > 1 ? 1 : 0) : (safeScale - 1) / (safeInitialScale - 1);
    const centerProgress = Math.max(0, Math.min(1, normalizedScale));
    const easedCenterProgress = centerProgress * centerProgress * (3 - 2 * centerProgress);
    const sourceCenterX = sourceBounds.left + sourceBounds.width / 2;
    const sourceCenterY = sourceBounds.top + sourceBounds.height / 2;
    const viewportCenterX = viewportLeft + Math.max(Number(viewportWidth) || 0, 0) / 2;
    const viewportCenterY = viewportTop + Math.max(Number(viewportHeight) || 0, 0) / 2;
    const centerX = sourceCenterX + (viewportCenterX - sourceCenterX) * easedCenterProgress;
    const centerY = sourceCenterY + (viewportCenterY - sourceCenterY) * easedCenterProgress;
    return {
      bounds: {
        left: centerX - sourceBounds.width / 2,
        top: centerY - sourceBounds.height / 2,
        width: sourceBounds.width,
        height: sourceBounds.height,
      },
      centerProgress: easedCenterProgress,
      returnProgress: 1 - easedCenterProgress,
      panDamping: easedCenterProgress,
      panX: (Number(userPanX) || 0) * easedCenterProgress,
      panY: (Number(userPanY) || 0) * easedCenterProgress,
    };
  };

  const pointerPairGeometry = (pointers) => {
    if (!Array.isArray(pointers) || pointers.length < 2) return null;
    const [first, second] = pointers;
    if (![first?.x, first?.y, second?.x, second?.y].every(Number.isFinite)) return null;
    return {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      midpoint: {
        x: (first.x + second.x) / 2,
        y: (first.y + second.y) / 2,
      },
    };
  };

  const pinchTransformFromStart = ({
    startScale,
    startPanX,
    startPanY,
    startDistance,
    currentDistance,
    startMidpoint,
    currentMidpoint,
    center,
    minimumScale = 1,
    maximumScale = 5,
  }) => {
    const safeStartScale = clampScale(startScale, minimumScale, maximumScale);
    const safeStartDistance = Math.max(Number(startDistance) || 0, 0.0001);
    const scale = clampScale(
      safeStartScale * (Math.max(Number(currentDistance) || 0, 0) / safeStartDistance),
      minimumScale,
      maximumScale,
    );
    const ratio = scale / safeStartScale;
    const centerX = Number(center?.x) || 0;
    const centerY = Number(center?.y) || 0;
    const initialMidpointX = Number(startMidpoint?.x) || 0;
    const initialMidpointY = Number(startMidpoint?.y) || 0;
    const nextMidpointX = Number(currentMidpoint?.x) || 0;
    const nextMidpointY = Number(currentMidpoint?.y) || 0;
    return {
      scale,
      x: nextMidpointX - centerX - ratio * (initialMidpointX - centerX - (Number(startPanX) || 0)),
      y: nextMidpointY - centerY - ratio * (initialMidpointY - centerY - (Number(startPanY) || 0)),
    };
  };

  const clampZoomOffset = (value, viewportSize, scale) => {
    const boundary = Math.max(0, (Math.max(viewportSize, 0) * (Math.max(scale, 1) - 1)) / 2);
    return Math.min(Math.max(value, -boundary), boundary);
  };

  const clampElevatedPan = ({
    panX,
    panY,
    bounds,
    scale,
    viewportWidth,
    viewportHeight,
    viewportLeft = 0,
    viewportTop = 0,
    strictBounds = false,
    minimumVisible = 72,
    fittedPanRatio = 0.18,
    allowFittedTravel = false,
  }) => {
    if (!bounds) return { x: 0, y: 0 };
    const safeScale = Math.max(scale || 1, 1);
    const scaledWidth = Math.max(bounds.width || 0, 1) * safeScale;
    const scaledHeight = Math.max(bounds.height || 0, 1) * safeScale;
    const centerX = (bounds.left || 0) + (bounds.width || 0) / 2;
    const centerY = (bounds.top || 0) + (bounds.height || 0) / 2;
    const safeViewportWidth = Math.max(viewportWidth || 0, 1);
    const safeViewportHeight = Math.max(viewportHeight || 0, 1);
    if (!strictBounds) {
      const visibleX = Math.min(minimumVisible, scaledWidth / 2);
      const visibleY = Math.min(minimumVisible, scaledHeight / 2);
      const minimumCenterX = viewportLeft + visibleX - scaledWidth / 2;
      const maximumCenterX = viewportLeft + safeViewportWidth - visibleX + scaledWidth / 2;
      const minimumCenterY = viewportTop + visibleY - scaledHeight / 2;
      const maximumCenterY = viewportTop + safeViewportHeight - visibleY + scaledHeight / 2;
      return {
        x: Math.min(maximumCenterX - centerX, Math.max(minimumCenterX - centerX, Number(panX) || 0)),
        y: Math.min(maximumCenterY - centerY, Math.max(minimumCenterY - centerY, Number(panY) || 0)),
      };
    }
    const viewportRight = viewportLeft + safeViewportWidth;
    const viewportBottom = viewportTop + safeViewportHeight;
    const centeredX = viewportLeft + safeViewportWidth / 2 - centerX;
    const centeredY = viewportTop + safeViewportHeight / 2 - centerY;
    const fittedTravel = (scaledSize, sourceSize, viewportSize) => {
      if (!allowFittedTravel || safeScale <= 1.000001) return 0;
      return Math.max(
        0,
        Math.min(
          scaledSize * fittedPanRatio,
          viewportSize * 0.12,
          (sourceSize * (safeScale - 1)) / 2,
        ),
      );
    };
    const fittedTravelX = fittedTravel(scaledWidth, Math.max(bounds.width || 0, 1), safeViewportWidth);
    const fittedTravelY = fittedTravel(scaledHeight, Math.max(bounds.height || 0, 1), safeViewportHeight);
    const minimumX =
      scaledWidth <= safeViewportWidth ? centeredX - fittedTravelX : viewportRight - centerX - scaledWidth / 2;
    const maximumX =
      scaledWidth <= safeViewportWidth ? centeredX + fittedTravelX : viewportLeft - centerX + scaledWidth / 2;
    const minimumY =
      scaledHeight <= safeViewportHeight ? centeredY - fittedTravelY : viewportBottom - centerY - scaledHeight / 2;
    const maximumY =
      scaledHeight <= safeViewportHeight ? centeredY + fittedTravelY : viewportTop - centerY + scaledHeight / 2;

    return {
      x: Math.min(maximumX, Math.max(minimumX, Number(panX) || 0)),
      y: Math.min(maximumY, Math.max(minimumY, Number(panY) || 0)),
    };
  };

  const centerBoundsInViewport = ({ bounds, viewportWidth, viewportHeight, viewportLeft = 0, viewportTop = 0 }) => {
    if (!bounds) return null;
    return {
      left: viewportLeft + (Math.max(viewportWidth || 0, 0) - bounds.width) / 2,
      top: viewportTop + (Math.max(viewportHeight || 0, 0) - bounds.height) / 2,
      width: bounds.width,
      height: bounds.height,
    };
  };

  global.ScoreggiaMediaCore = {
    carouselDistanceThreshold,
    carouselGestureProfile,
    carouselReleaseDuration,
    centerBoundsInViewport,
    clampElevatedPan,
    clampScale,
    clampZoomOffset,
    containedImageBounds,
    desktopZoomReturnGeometry,
    gestureIntent,
    loopNavigationPlan,
    normalizeWheelDelta,
    normalizeProductOptionValueId,
    physicalIndexForLogical,
    productLinkForColor,
    productLinkForSlide,
    pinchTransformFromStart,
    pointerPairGeometry,
    recentPointerVelocity,
    recentPointerTrajectory,
    recentVelocityMaximumAge,
    recentTrajectoryWindow,
    recentVelocityWindow,
    recordRecentPointerSample,
    swipeDirection,
    responsiveSwipeDirection,
    responsiveTouchDistanceThreshold,
    responsiveTouchGestureProfile,
    wheelScaleFromDelta,
    wrapIndex,
    zoomTransformAroundPoint,
    zoomGestureOutcome,
  };

  if (!global.HTMLElement || !global.customElements) return;

  class CircularCarousel {
    constructor(root, options = {}) {
      this.root = root;
      this.options = options;
      this.track = root.querySelector(options.trackSelector || '[data-scoreggia-media-track]');
      this.viewport = root.querySelector(options.viewportSelector || '[data-scoreggia-media-viewport]');
      this.slides = Array.from(root.querySelectorAll(options.slideSelector || '[data-scoreggia-media-slide]'));
      this.thumbnails = Array.from(root.querySelectorAll(options.thumbnailSelector || '[data-scoreggia-media-thumbnail]'));
      this.previousButton = root.querySelector(options.previousSelector || '[data-scoreggia-media-previous]');
      this.nextButton = root.querySelector(options.nextSelector || '[data-scoreggia-media-next]');
      this.currentOutput = root.querySelector(options.currentSelector || '[data-scoreggia-media-current]');
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.pointer = null;
      this.suppressNextClick = false;
      this.suppressClickTarget = null;
      this.ignoreMouseUntil = 0;
      this.abortController = null;
      this.loopClones = [];
      this.loopCorrection = null;
      this.loopCorrectionTimer = null;
      this.transitionRestoreFrame = null;
      this.releaseDurationTimer = null;
      this.gestureRenderFrame = null;
      this.pendingNavigations = [];
      this.currentPhysicalIndex = this.physicalIndex(this.activeIndex);
    }

    connect() {
      if (this.abortController || !this.track || !this.viewport || !this.slides.length) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.createLoopClones();

      this.previousButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');
      this.previousButton?.addEventListener('click', () => this.move(-1, { user: true, source: 'previous' }), {
        signal,
      });
      this.nextButton?.addEventListener('click', () => this.move(1, { user: true, source: 'next' }), { signal });

      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.goTo(index, { user: true, source: 'thumbnail' });
          },
          { signal },
        );
      });

      this.root.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      this.track.addEventListener('transitionend', (event) => this.handleTrackTransitionEnd(event), { signal });
      if ('PointerEvent' in global) {
        this.viewport.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
        this.viewport.addEventListener('pointermove', (event) => this.handlePointerMove(event), {
          passive: false,
          signal,
        });
        this.viewport.addEventListener('pointerup', (event) => this.handlePointerEnd(event), { signal });
        this.viewport.addEventListener('pointercancel', (event) => this.handlePointerCancel(event), { signal });
        this.viewport.addEventListener('lostpointercapture', (event) => this.handleLostPointerCapture(event), {
          signal,
        });
      } else {
        this.viewport.addEventListener('touchstart', (event) => this.handleTouchStart(event), {
          passive: true,
          signal,
        });
        this.viewport.addEventListener('touchmove', (event) => this.handleTouchMove(event), {
          passive: false,
          signal,
        });
        this.viewport.addEventListener('touchend', (event) => this.handleTouchEnd(event), { signal });
        this.viewport.addEventListener('touchcancel', () => this.cancelPointer(), { signal });
        this.viewport.addEventListener('mousedown', (event) => this.handleMouseDown(event), { signal });
        this.viewport.addEventListener('mousemove', (event) => this.handleMouseMove(event), {
          passive: false,
          signal,
        });
        this.viewport.addEventListener('mouseup', (event) => this.handleMouseEnd(event), { signal });
        this.viewport.addEventListener('mouseleave', () => this.cancelPointer(), { signal });
      }
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener('click', (event) => this.handleViewportClick(event), { capture: true, signal });
      const cancelForViewportChange = () => {
        this.cancelPointer(true, true, true);
        this.clearClickSuppression();
      };
      global.addEventListener('resize', cancelForViewportChange, { passive: true, signal });
      global.addEventListener('orientationchange', cancelForViewportChange, { passive: true, signal });
      global.visualViewport?.addEventListener('resize', cancelForViewportChange, { passive: true, signal });
      global.visualViewport?.addEventListener('scroll', cancelForViewportChange, { passive: true, signal });
      global.document?.addEventListener(
        'shopify:section:unload',
        (event) => {
          if (event.target?.contains?.(this.root) || this.root.contains?.(event.target)) cancelForViewportChange();
        },
        { signal },
      );

      this.goTo(this.activeIndex, { immediate: true, source: 'initialize' });
    }

    disconnect() {
      this.abortController?.abort();
      this.abortController = null;
      this.cancelPointer();
      this.clearClickSuppression();
      this.clearLoopState();
      this.clearReleaseDuration();
      this.cleanupLoopClones();
    }

    refresh() {
      const activeMediaId = this.slides[this.activeIndex]?.dataset.mediaId;
      this.prepareForMutation();
      this.slides = Array.from(this.root.querySelectorAll(this.options.slideSelector || '[data-scoreggia-media-slide]'));
      this.thumbnails = Array.from(
        this.root.querySelectorAll(this.options.thumbnailSelector || '[data-scoreggia-media-thumbnail]'),
      );
      const refreshedIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === activeMediaId);
      this.createLoopClones();
      this.goTo(refreshedIndex >= 0 ? refreshedIndex : 0, { immediate: true, source: 'refresh' });
    }

    prepareForMutation() {
      if (!this.track) return;
      this.cancelPointer(true, true, true);
      this.clearLoopState();
      this.cleanupLoopClones();
      this.setImmediatePosition(Math.max(0, this.activeIndex));
    }

    createLoopClones() {
      this.cleanupLoopClones();
      if (this.options.loop === false || !this.track || this.slides.length <= 1) {
        this.currentPhysicalIndex = Math.max(0, this.activeIndex);
        return;
      }

      const lastClone = this.createLoopClone(this.slides[this.slides.length - 1], 'last');
      const firstClone = this.createLoopClone(this.slides[0], 'first');
      this.track.insertBefore(lastClone, this.track.firstChild);
      this.track.append(firstClone);
      this.loopClones = [lastClone, firstClone];
      this.currentPhysicalIndex = this.physicalIndex(this.activeIndex);
    }

    createLoopClone(slide, cloneType) {
      const clone = slide.cloneNode(true);
      const nodes = [clone, ...clone.querySelectorAll('*')];
      nodes.forEach((node) => {
        Array.from(node.attributes || []).forEach((attribute) => {
          if (attribute.name === 'id' || attribute.name.startsWith('data-') || attribute.name.startsWith('aria-')) {
            node.removeAttribute(attribute.name);
          }
        });
        node.classList?.remove('is-active', 'is-dragging', 'is-zoomed', 'is-scoreggia-elevated-zoom-source');
        if (node.matches?.('a')) node.removeAttribute('href');
        if (node.matches?.('button, input, select, textarea')) node.disabled = true;
        if (node.matches?.('[contenteditable]')) node.setAttribute('contenteditable', 'false');
        if (
          node.matches?.(
            'a, button, input, select, textarea, iframe, summary, audio[controls], video[controls], [contenteditable], [tabindex]',
          )
        ) {
          node.setAttribute('tabindex', '-1');
        }
      });
      clone.dataset.scoreggiaLoopClone = cloneType;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.setAttribute('role', 'presentation');
      return clone;
    }

    cleanupLoopClones() {
      this.track?.querySelectorAll('[data-scoreggia-loop-clone]').forEach((clone) => clone.remove());
      this.loopClones = [];
    }

    move(direction, options = {}) {
      this.goTo(this.activeIndex + direction, { ...options, direction });
    }

    physicalIndex(index) {
      return this.options.loop === false ? Math.max(0, Math.min(index, this.slides.length - 1)) : physicalIndexForLogical(index, this.slides.length);
    }

    goTo(index, options = {}) {
      if (!this.slides.length) return;
      if (this.loopCorrection && !options.replayQueuedNavigation) {
        const lastQueuedIndex = this.pendingNavigations[this.pendingNavigations.length - 1]?.index;
        const queueBaseIndex = Number.isInteger(lastQueuedIndex) ? lastQueuedIndex : this.activeIndex;
        const queuedIndex = options.direction
          ? wrapIndex(queueBaseIndex + options.direction, this.slides.length)
          : wrapIndex(index, this.slides.length);
        this.pendingNavigations.push({ index: queuedIndex, options });
        if (this.pendingNavigations.length > 12) this.pendingNavigations.shift();
        return;
      }
      const nextIndex =
        this.options.loop === false
          ? Math.max(0, Math.min(index, this.slides.length - 1))
          : wrapIndex(index, this.slides.length);
      const previousIndex = this.activeIndex;
      const animate = !options.immediate && !this.reducedMotion;
      const navigationPlan =
        this.options.loop === false
          ? { physicalIndex: nextIndex, correctionPhysicalIndex: null }
          : loopNavigationPlan({
              previousIndex,
              nextIndex,
              direction: options.direction || 0,
              count: this.slides.length,
              animate,
            });
      if (options.user) this.signalInteraction(options.source || 'navigation');
      this.options.beforeChange?.(nextIndex, previousIndex, options);
      this.activeIndex = nextIndex;
      if (animate) this.prepareAnimatedPosition(options.releaseDuration);
      else this.setImmediatePosition(navigationPlan.physicalIndex);
      if (animate) this.setTrackPosition(navigationPlan.physicalIndex);
      this.track.style.setProperty('--scoreggia-media-index', String(nextIndex));

      if (navigationPlan.correctionPhysicalIndex !== null) {
        this.startLoopCorrection(navigationPlan.correctionPhysicalIndex);
      }

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });
      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === nextIndex;
        if (active) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
        if (thumbnail.matches?.('button')) thumbnail.setAttribute('aria-pressed', String(active));
      });
      if (this.currentOutput) this.currentOutput.textContent = String(nextIndex + 1);
      if (this.options.loop === false) {
        if (this.previousButton) this.previousButton.disabled = nextIndex === 0;
        if (this.nextButton) this.nextButton.disabled = nextIndex === this.slides.length - 1;
      }

      const activeThumbnail = this.thumbnails[nextIndex];
      if (activeThumbnail && options.scrollThumbnail !== false) {
        this.scrollThumbnailHorizontally(activeThumbnail, options);
      }
      this.options.afterChange?.(nextIndex, previousIndex, options);
    }

    setTrackPosition(physicalIndex) {
      this.currentPhysicalIndex = physicalIndex;
      this.track.style.transform = `translate3d(${-100 * physicalIndex}%, 0, 0)`;
    }

    setImmediatePosition(physicalIndex, afterRestore) {
      if (!this.track) return;
      this.clearReleaseDuration();
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.track.classList.remove('is-dragging');
      this.track.classList.add('is-immediate');
      this.setTrackPosition(physicalIndex);
      this.track.getBoundingClientRect();
      this.transitionRestoreFrame = global.requestAnimationFrame(() => {
        this.transitionRestoreFrame = null;
        this.track?.classList.remove('is-immediate');
        afterRestore?.();
      });
    }

    prepareAnimatedPosition(releaseDuration) {
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.applyReleaseDuration(releaseDuration);
      this.track.classList.remove('is-immediate', 'is-dragging');
      this.track.getBoundingClientRect();
    }

    startLoopCorrection(correctionPhysicalIndex) {
      this.clearLoopCorrection();
      this.loopCorrection = { correctionPhysicalIndex };
      this.loopCorrectionTimer = global.setTimeout(() => this.finishLoopCorrection(), 500);
    }

    handleTrackTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.clearReleaseDuration();
      if (this.loopCorrection) this.finishLoopCorrection();
    }

    finishLoopCorrection() {
      const correction = this.loopCorrection;
      if (!correction) return;
      this.clearLoopCorrection();
      this.setImmediatePosition(correction.correctionPhysicalIndex, () => this.replayNextNavigation());
    }

    replayNextNavigation() {
      const queuedNavigation = this.pendingNavigations[this.pendingNavigations.length - 1];
      this.pendingNavigations = [];
      if (!queuedNavigation) return;
      this.goTo(queuedNavigation.index, { ...queuedNavigation.options, replayQueuedNavigation: true });
    }

    clearLoopCorrection() {
      if (this.loopCorrectionTimer !== null) global.clearTimeout(this.loopCorrectionTimer);
      this.loopCorrectionTimer = null;
      this.loopCorrection = null;
    }

    clearLoopState() {
      this.clearLoopCorrection();
      this.pendingNavigations = [];
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.clearReleaseDuration();
    }

    scrollThumbnailHorizontally(thumbnail, options = {}) {
      const rail = thumbnail.closest('.scoreggia-card-media__thumbnail-rail, .scoreggia-product-media__thumbnail-list');
      const item = thumbnail.closest('.scoreggia-product-media__thumbnail-item') || thumbnail;
      if (!rail || !item || typeof rail.scrollTo !== 'function') return;
      const railBounds = rail.getBoundingClientRect();
      const itemBounds = item.getBoundingClientRect();
      let left = rail.scrollLeft;
      if (itemBounds.left < railBounds.left) left -= railBounds.left - itemBounds.left;
      else if (itemBounds.right > railBounds.right) left += itemBounds.right - railBounds.right;
      if (left === rail.scrollLeft) return;
      rail.scrollTo({
        left,
        behavior: this.reducedMotion || options.immediate ? 'auto' : 'smooth',
      });
    }

    signalInteraction(source) {
      this.options.onInteraction?.(source);
    }

    handleViewportClick(event) {
      if (!this.suppressNextClick) return;
      const suppressionTarget = this.suppressClickTarget;
      this.clearClickSuppression();
      const targetMatches =
        !suppressionTarget ||
        event.target === suppressionTarget ||
        suppressionTarget.contains?.(event.target) ||
        event.target?.contains?.(suppressionTarget);
      if (!targetMatches) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    handleKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('input, select, textarea, video, model-viewer, iframe, [contenteditable="true"]')) {
        return;
      }

      const thumbnailIndex = this.thumbnails.indexOf(event.target);
      if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') && thumbnailIndex >= 0) {
        event.preventDefault();
        this.goTo(thumbnailIndex, { user: true, source: 'thumbnail' });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1, { user: true, source: 'keyboard' });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1, { user: true, source: 'keyboard' });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, { user: true, immediate: true, source: 'keyboard' });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, { user: true, immediate: true, source: 'keyboard' });
      } else if (event.key === 'Escape' && this.options.onEscape?.()) {
        event.preventDefault();
        this.signalInteraction('escape');
      }
    }

    handlePointerDown(event) {
      if (this.pointer && event.pointerId !== this.pointer.id) {
        this.cancelPointer(true, true, true);
        this.clearClickSuppression();
        return;
      }
      if (event.isPrimary === false) {
        if (event.pointerType === 'touch' && this.pointer?.pointerType === 'touch') this.cancelPointer();
        return;
      }
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const started = this.startPointer(
        event.pointerId,
        event.clientX,
        event.clientY,
        event.target,
        event.pointerType || 'pointer',
      );
      if (!started) return;
      if (event.pointerType === 'touch') this.signalInteraction('touch');
    }

    handlePointerMove(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.updatePointer(event.clientX, event.clientY, event, true, event.timeStamp);
    }

    handlePointerEnd(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.finishPointer(event.clientX, event.clientY, event.timeStamp);
    }

    handlePointerCancel(event) {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      this.cancelPointer(true, true, true);
    }

    handleLostPointerCapture(event) {
      if (this.pointer?.id !== event.pointerId) return;
      this.cancelPointer(true, true, true);
    }

    handleTouchStart(event) {
      if (event.touches.length !== 1) {
        this.cancelPointer();
        return;
      }
      const touch = event.changedTouches[0];
      this.ignoreMouseUntil = global.performance.now() + 800;
      const started = this.startPointer(`touch-${touch.identifier}`, touch.clientX, touch.clientY, event.target, 'touch');
      if (started) this.signalInteraction('touch');
    }

    handleTouchMove(event) {
      if (!this.pointer?.id?.startsWith('touch-')) return;
      const identifier = Number.parseInt(this.pointer.id.slice(6), 10);
      const touch = Array.from(event.touches).find((item) => item.identifier === identifier);
      if (touch) this.updatePointer(touch.clientX, touch.clientY, event, false);
    }

    handleTouchEnd(event) {
      if (!this.pointer?.id?.startsWith('touch-')) return;
      const identifier = Number.parseInt(this.pointer.id.slice(6), 10);
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === identifier);
      if (touch) this.finishPointer(touch.clientX, touch.clientY);
      else this.cancelPointer();
    }

    handleMouseDown(event) {
      if (event.button !== 0 || global.performance.now() < this.ignoreMouseUntil) return;
      this.startPointer('mouse', event.clientX, event.clientY, event.target, 'mouse');
    }

    handleMouseMove(event) {
      if (this.pointer?.id !== 'mouse') return;
      this.updatePointer(event.clientX, event.clientY, event, false);
    }

    handleMouseEnd(event) {
      if (this.pointer?.id !== 'mouse') return;
      this.finishPointer(event.clientX, event.clientY);
    }

    startPointer(id, clientX, clientY, target, pointerType) {
      this.clearClickSuppression();
      if (this.options.isZoomed?.()) {
        this.signalInteraction('zoom');
        return false;
      }
      if (target.closest?.('[data-scoreggia-media-control]')) return false;
      const interactiveTarget = target.closest?.('a, button, input, select, textarea, summary, [contenteditable="true"]');
      const gestureSurface = this.options.gestureSurfaceSelector
        ? target.closest?.(this.options.gestureSurfaceSelector)
        : null;
      if (interactiveTarget && !gestureSurface) return false;
      if (target.closest?.('video, model-viewer, iframe')) {
        this.signalInteraction('native-media');
        return false;
      }
      if (this.slides.length <= 1) return false;
      this.cancelPointer(true, true, true);
      const startedAt = global.performance.now();
      this.pointer = {
        id,
        pointerType,
        captureTarget: gestureSurface?.setPointerCapture
          ? gestureSurface
          : target?.setPointerCapture
            ? target
            : this.viewport,
        suppressionTarget: gestureSurface || target,
        startX: clientX,
        startY: clientY,
        lastX: clientX,
        lastY: clientY,
        startedAt,
        actualX: clientX,
        actualY: clientY,
        actualTime: startedAt,
        viewportWidth: Math.max(this.viewport.clientWidth || 0, 1),
        intent: null,
        meaningfulSamples: 0,
        visualOffset: 0,
        moved: false,
        captured: false,
        samples: recordRecentPointerSample([], clientX, startedAt, clientY),
      };
      return true;
    }

    updatePointer(clientX, clientY, event, canCapture, eventTimeStamp = null) {
      const pointer = this.pointer;
      if (!pointer || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
      const deltaX = clientX - pointer.startX;
      const deltaY = clientY - pointer.startY;
      const currentTime = global.performance.now();
      const sampleTime = normalizePointerTimestamp(eventTimeStamp, currentTime);
      pointer.samples = recordRecentPointerSample(pointer.samples, clientX, sampleTime, clientY);
      pointer.actualX = clientX;
      pointer.actualY = clientY;
      pointer.actualTime = sampleTime;

      if (!pointer.intent) {
        const profile = pointer.pointerType === 'mouse' ? carouselGestureProfile('mouse') : responsiveTouchGestureProfile;
        if (Math.hypot(deltaX, deltaY) > profile.deadZone) pointer.meaningfulSamples += 1;
        const trajectory = recentPointerTrajectory({ samples: pointer.samples, now: sampleTime });
        const intent = gestureIntent({
          deltaX,
          deltaY,
          threshold: profile.deadZone,
          ratio: profile.horizontalRatio,
          verticalRatio: profile.verticalRatio,
          maximumUndecidedDistance: profile.maximumUndecidedDistance,
          undecidedSamples: pointer.meaningfulSamples,
          maximumUndecidedSamples: profile.maximumUndecidedSamples,
          recentDeltaX: trajectory.x,
          recentDeltaY: trajectory.y,
        });
        if (intent === 1) pointer.intent = 'horizontal';
        else if (intent === -1) {
          pointer.intent = 'vertical';
          this.cancelPointer(true, true, true);
          return;
        }
      }

      if (pointer.intent === 'horizontal') {
        pointer.moved = true;
        if (canCapture) this.capturePointer(event.pointerId);
        if (event.cancelable) event.preventDefault();
        pointer.visualOffset = deltaX;
        this.scheduleDragFeedback();
      } else if (this.track?.classList.contains('is-dragging')) {
        pointer.visualOffset = 0;
        this.scheduleDragFeedback();
      }

      pointer.lastX = clientX;
      pointer.lastY = clientY;
    }

    finishPointer(clientX, clientY, eventTimeStamp = null) {
      const pointer = this.pointer;
      if (!pointer) return;
      const deltaX = clientX - pointer.startX;
      const deltaY = clientY - pointer.startY;
      const finishedAt = global.performance.now();
      const finalTime = normalizePointerTimestamp(eventTimeStamp, finishedAt);
      if (clientX !== pointer.actualX || clientY !== pointer.actualY) {
        pointer.samples = recordRecentPointerSample(pointer.samples, clientX, finalTime, clientY);
      }
      pointer.actualX = clientX;
      pointer.actualY = clientY;
      pointer.actualTime = finalTime;
      const velocityX = recentPointerVelocity({ samples: pointer.samples, now: finishedAt });
      const recognizedDrag = pointer.intent === 'horizontal';
      const direction = recognizedDrag
        ? responsiveSwipeDirection({
            deltaX,
            deltaY,
            duration: finishedAt - pointer.startedAt,
            width: pointer.viewportWidth,
            pointerType: pointer.pointerType,
            velocityX,
            horizontalIntent: true,
          })
        : 0;
      const releaseDuration = carouselReleaseDuration({
        width: pointer.viewportWidth,
        deltaX,
        velocityX,
        accepted: Boolean(direction),
      });

      if (recognizedDrag) this.markClickForSuppression(pointer.suppressionTarget);
      if (recognizedDrag) {
        this.clearGestureRenderFrame();
        pointer.visualOffset = deltaX;
        this.setDragFeedback(deltaX);
      }
      this.cancelPointer(true, !recognizedDrag, !recognizedDrag);
      if (direction) {
        this.move(direction, { user: true, source: 'swipe', releaseDuration });
      } else if (recognizedDrag) {
        this.restoreTrackAfterDrag({ releaseDuration });
        this.signalInteraction('drag');
      }
    }

    capturePointer(pointerId) {
      const pointer = this.pointer;
      const captureTarget = pointer?.captureTarget;
      if (!pointer || pointer.captured || !captureTarget?.setPointerCapture || captureTarget.isConnected === false) return;
      try {
        captureTarget.setPointerCapture(pointerId);
        pointer.captured = captureTarget.hasPointerCapture ? captureTarget.hasPointerCapture(pointerId) : true;
      } catch {
        pointer.captured = false;
      }
    }

    setDragFeedback(deltaX) {
      if (!this.track || this.loopCorrection) return;
      const basePhysicalIndex = this.physicalIndex(this.activeIndex);
      const viewportWidth = Math.max(this.pointer?.viewportWidth || this.viewport.clientWidth || 0, 1);
      const offset = Math.max(-viewportWidth, Math.min(viewportWidth, deltaX));
      if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
      this.transitionRestoreFrame = null;
      this.track.classList.remove('is-immediate');
      this.track.classList.add('is-dragging');
      this.track.style.transform = `translate3d(calc(${-100 * basePhysicalIndex}% + ${offset}px), 0, 0)`;
    }

    scheduleDragFeedback() {
      if (this.track && !this.loopCorrection) {
        if (this.transitionRestoreFrame !== null) global.cancelAnimationFrame(this.transitionRestoreFrame);
        this.transitionRestoreFrame = null;
        this.track.classList.remove('is-immediate');
        this.track.classList.add('is-dragging');
      }
      if (this.gestureRenderFrame !== null) return;
      this.gestureRenderFrame = global.requestAnimationFrame(() => {
        this.gestureRenderFrame = null;
        if (!this.pointer) return;
        this.setDragFeedback(this.pointer.visualOffset);
      });
    }

    clearGestureRenderFrame() {
      if (this.gestureRenderFrame !== null) global.cancelAnimationFrame(this.gestureRenderFrame);
      this.gestureRenderFrame = null;
    }

    restoreTrackAfterDrag({ immediate = false, releaseDuration } = {}) {
      if (!this.track?.classList.contains('is-dragging')) return;
      this.track.classList.remove('is-dragging');
      this.track.getBoundingClientRect();
      const physicalIndex = this.physicalIndex(this.activeIndex);
      if (immediate) this.setImmediatePosition(physicalIndex);
      else {
        this.applyReleaseDuration(releaseDuration);
        this.setTrackPosition(physicalIndex);
      }
    }

    applyReleaseDuration(duration) {
      this.clearReleaseDuration();
      if (!Number.isFinite(duration) || !this.track) return;
      this.track.style.setProperty('--scoreggia-media-release-duration', `${duration}ms`);
      this.releaseDurationTimer = global.setTimeout(() => this.clearReleaseDuration(), duration + 80);
    }

    clearReleaseDuration() {
      if (this.releaseDurationTimer !== null) global.clearTimeout(this.releaseDurationTimer);
      this.releaseDurationTimer = null;
      this.track?.style?.removeProperty?.('--scoreggia-media-release-duration');
    }

    markClickForSuppression(target) {
      this.clearClickSuppression();
      this.suppressNextClick = true;
      this.suppressClickTarget = target || null;
    }

    clearClickSuppression() {
      this.suppressNextClick = false;
      this.suppressClickTarget = null;
    }

    cancelPointer(releaseCapture = true, restoreDrag = true, immediateRestore = false) {
      const pointer = this.pointer;
      this.clearGestureRenderFrame();
      if (pointer) {
        pointer.visualOffset = 0;
      }
      this.pointer = null;
      if (restoreDrag) this.restoreTrackAfterDrag({ immediate: immediateRestore });
      const captureTarget = pointer?.captureTarget;
      if (!releaseCapture || !captureTarget?.releasePointerCapture || captureTarget.isConnected === false) return;
      let hasCapture = pointer.captured;
      if (captureTarget.hasPointerCapture) {
        try {
          hasCapture = captureTarget.hasPointerCapture(pointer.id);
        } catch {
          hasCapture = false;
        }
      }
      if (!hasCapture) return;
      try {
        captureTarget.releasePointerCapture(pointer.id);
      } catch {
        return;
      }
    }

    get reducedMotion() {
      return global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  global.ScoreggiaMediaCore.CircularCarousel = CircularCarousel;
  global.ScoreggiaMediaCore.normalizeDiscoveryPayload = normalizeDiscoveryPayload;

  class ScoreggiaCardMedia extends global.HTMLElement {
    connectedCallback() {
      if (this.lifecycleController) return;
      this.lifecycleController = new AbortController();
      this.dataset.scoreggiaDiscoveryInitialized = 'true';
      this.productLink = this.querySelector('[data-card-media-product-link]');
      this.track = this.querySelector('[data-card-media-track]');
      this.thumbnailRail = this.querySelector('[data-card-media-thumbnail-rail]');
      this.mediaControls = this.querySelector('[data-card-media-controls]');
      this.mediaTotal = this.querySelector('[data-card-media-total]');
      this.styleButtons = Array.from(this.querySelectorAll('[data-scoreggia-card-style-option]'));
      this.cardLinks = this.collectCardLinks();
      this.styleStates = new Map();
      const activeStyleId = String(this.dataset.activeStyleId || '');
      if (activeStyleId) this.styleStates.set(activeStyleId, this.captureInitialStyleState(activeStyleId));
      this.readDiscoveryPayload().forEach((style) => this.styleStates.set(style.id, style));
      const activeStyle = this.styleStates.get(activeStyleId);
      if (activeStyle) {
        this.establishActiveStyle(
          activeStyle,
          this.querySelector('[data-card-media-slide].is-active') || this.querySelector('[data-card-media-slide]'),
        );
      }
      this.addEventListener('click', (event) => this.handleStyleButton(event), {
        signal: this.lifecycleController.signal,
      });
      this.connectCarousel();
      this.syncStyleButtons(activeStyleId);
    }

    connectCarousel() {
      this.controller = new CircularCarousel(this, {
        trackSelector: '[data-card-media-track]',
        viewportSelector: '[data-card-media-viewport]',
        slideSelector: '[data-card-media-slide]',
        thumbnailSelector: '[data-card-media-thumbnail]',
        previousSelector: '[data-card-media-previous]',
        nextSelector: '[data-card-media-next]',
        currentSelector: '[data-card-media-current]',
        gestureSurfaceSelector: '[data-card-media-product-link]',
        afterChange: (nextIndex) => this.updateProductLink(nextIndex),
        loop: true,
      });
      this.controller.connect();
      this.syncMediaControls();
    }

    captureInitialStyleState(styleId) {
      return {
        id: styleId,
        label:
          this.styleButtons.find((button) => String(button.dataset.styleId) === styleId)?.textContent.trim() || '',
        title: this.dataset.productTitle || '',
        url: this.productLink?.dataset.productUrl || this.productLink?.getAttribute('href') || '',
        defaultMediaId:
          this.querySelector('[data-card-media-slide].is-active')?.dataset.mediaId ||
          this.querySelector('[data-card-media-slide]')?.dataset.mediaId ||
          '',
        defaultColorOptionValueId: this.dataset.activeColorOptionValueId || '',
        initialSlides: Array.from(this.querySelectorAll('[data-card-media-slide]')).map((slide) =>
          slide.cloneNode(true)
        ),
        initialThumbnails: Array.from(this.querySelectorAll('[data-card-media-thumbnail]')).map((thumbnail) =>
          thumbnail.cloneNode(true)
        ),
      };
    }

    readDiscoveryPayload() {
      const source = this.querySelector('[data-scoreggia-discovery-payload]');
      if (!source) return [];
      try {
        return normalizeDiscoveryPayload(JSON.parse(source.textContent));
      } catch {
        this.dataset.scoreggiaDiscoveryPayloadError = 'true';
        return [];
      }
    }

    handleStyleButton(event) {
      const button = event.target.closest('[data-scoreggia-card-style-option]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.activateStyle(String(button.dataset.styleId || ''));
    }

    activateStyle(styleId) {
      if (!styleId || styleId === String(this.dataset.activeStyleId || '')) return false;
      const style = this.styleStates.get(styleId);
      if (!style || !this.track || !this.isConnected) return false;

      const state = style.initialSlides ? this.cloneInitialStyleState(style) : this.createStyleState(style);
      if (!state.slides.length) return false;
      this.controller?.disconnect();
      this.track.replaceChildren(...state.slides);
      if (this.thumbnailRail) this.thumbnailRail.replaceChildren(...state.thumbnails);
      this.dataset.activeStyleId = style.id;
      this.dataset.activeStyleLabel = style.label;
      this.dataset.productTitle = style.title;
      this.productLink.dataset.productUrl = style.url;
      this.establishActiveStyle(style, state.slides[state.activeIndex]);
      this.syncStyleButtons(style.id);
      this.connectCarousel();
      this.dispatchEvent(
        new CustomEvent('scoreggia-card:style-changed', {
          bubbles: true,
          detail: { styleId: style.id, styleLabel: style.label, productUrl: style.url },
        }),
      );
      return true;
    }

    cloneInitialStyleState(style) {
      const slides = style.initialSlides.map((slide) => slide.cloneNode(true));
      return {
        slides,
        thumbnails: style.initialThumbnails.map((thumbnail) => thumbnail.cloneNode(true)),
        activeIndex: Math.max(0, slides.findIndex((slide) => slide.classList?.contains?.('is-active'))),
      };
    }

    createStyleState(style) {
      const defaultIndex = Math.max(
        0,
        style.media.findIndex((media) => media.id === style.defaultMediaId),
      );
      return {
        slides: style.media.map((media, index) => this.createStyleSlide(style, media, index, index === defaultIndex)),
        thumbnails: this.thumbnailRail
          ? style.media.map((media, index) => this.createStyleThumbnail(style, media, index, index === defaultIndex))
          : [],
        activeIndex: defaultIndex,
      };
    }

    createStyleSlide(style, media, index, active) {
      const slide = document.createElement('div');
      slide.id = `${this.dataset.carouselId}-Style-${style.id}-Slide-${index + 1}`;
      slide.className = `scoreggia-card-media__slide${active ? ' is-active' : ''}`;
      slide.dataset.cardMediaSlide = '';
      slide.dataset.logicalIndex = String(index);
      slide.dataset.mediaId = media.id;
      slide.dataset.mediaPosition = String(media.position || index + 1);
      slide.dataset.mediaType = media.type;
      if (media.colorOptionValueId) slide.dataset.colorOptionValueId = media.colorOptionValueId;
      slide.setAttribute('aria-hidden', String(!active));

      const image = document.createElement('img');
      image.className = 'scoreggia-card-media__image motion-reduce';
      image.src = media.src;
      if (media.srcset) image.srcset = media.srcset;
      image.sizes = '(min-width: 1280px) min(33vw, 590px), (min-width: 640px) 50vw, 100vw';
      image.alt = media.alt;
      image.loading = active ? 'eager' : 'lazy';
      image.decoding = 'async';
      if (media.width) image.width = media.width;
      if (media.height) image.height = media.height;
      slide.append(image);
      return slide;
    }

    createStyleThumbnail(style, media, index, active) {
      const thumbnail = document.createElement('button');
      thumbnail.className = 'scoreggia-card-media__thumbnail';
      thumbnail.type = 'button';
      thumbnail.dataset.cardMediaThumbnail = '';
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      thumbnail.setAttribute('aria-pressed', String(active));
      const label = String(this.dataset.thumbnailLabelTemplate || 'View image __INDEX__').replace(
        '__INDEX__',
        String(index + 1),
      );
      thumbnail.setAttribute('aria-label', `${label} — ${style.title}`);

      const image = document.createElement('img');
      image.className = 'scoreggia-card-media__thumbnail-image';
      image.src = media.thumbnailSrc || media.src;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      thumbnail.append(image);
      return thumbnail;
    }

    syncStyleButtons(styleId) {
      this.styleButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(String(button.dataset.styleId) === String(styleId)));
      });
    }

    syncMediaControls() {
      const count = this.controller?.slides.length || 0;
      if (this.mediaControls) this.mediaControls.hidden = count <= 1;
      if (this.mediaTotal) this.mediaTotal.textContent = String(count);
    }

    establishActiveStyle(style, activeSlide) {
      this.activeStyleState = style;
      this.activeStyleColorOptionValueIds = styleColorOptionValueIds(style);
      const defaultColorId = normalizeProductOptionValueId(style.defaultColorOptionValueId);
      const mediaColorId = normalizeProductOptionValueId(activeSlide?.dataset?.colorOptionValueId);
      this.currentColorOptionValueId = this.activeStyleColorOptionValueIds.has(defaultColorId)
        ? defaultColorId
        : this.activeStyleColorOptionValueIds.has(mediaColorId)
          ? mediaColorId
          : '';
    }

    collectCardLinks() {
      return Array.from(
        new Set(
          [
            this.productLink,
            ...Array.from(
              this.closest('[data-scoreggia-discovery-card], .card-wrapper')?.querySelectorAll(
                '[data-scoreggia-card-product-link]',
              ) || [],
            ),
          ].filter(Boolean),
        ),
      );
    }

    updateProductLink(index) {
      if (!this.productLink) return;
      const activeSlide = this.controller?.slides[index];
      const activeMediaColorId = normalizeProductOptionValueId(activeSlide?.dataset.colorOptionValueId);
      if (this.activeStyleColorOptionValueIds?.has(activeMediaColorId)) {
        this.currentColorOptionValueId = activeMediaColorId;
      } else if (!this.activeStyleColorOptionValueIds?.has(this.currentColorOptionValueId)) {
        this.currentColorOptionValueId = '';
      }
      const nextUrl = productLinkForColor(
        this.dataset.scoreggiaStyleDetails === 'true'
          ? productLinkForSlide(this.productLink.dataset.productUrl, activeSlide)
          : this.activeStyleState?.url || this.productLink.dataset.productUrl,
        this.currentColorOptionValueId,
        this.activeStyleColorOptionValueIds,
      );
      if (!nextUrl) return;
      if (this.currentColorOptionValueId) this.dataset.activeColorOptionValueId = this.currentColorOptionValueId;
      else delete this.dataset.activeColorOptionValueId;
      this.cardLinks = this.collectCardLinks();
      this.cardLinks.forEach((link) => link.setAttribute('href', nextUrl));
    }

    disconnectedCallback() {
      this.lifecycleController?.abort();
      this.lifecycleController = null;
      this.controller?.disconnect();
      this.controller = null;
      this.productLink = null;
      this.track = null;
      this.thumbnailRail = null;
      this.mediaControls = null;
      this.mediaTotal = null;
      this.styleButtons = [];
      this.cardLinks = [];
      this.styleStates?.clear();
      this.styleStates = null;
      this.activeStyleState = null;
      this.activeStyleColorOptionValueIds = null;
      this.currentColorOptionValueId = '';
      delete this.dataset.scoreggiaDiscoveryInitialized;
    }
  }
  global.ScoreggiaMediaCore.ScoreggiaCardMedia = ScoreggiaCardMedia;
  if (!global.customElements.get('scoreggia-card-media')) {
    global.customElements.define('scoreggia-card-media', ScoreggiaCardMedia);
  }
})(typeof window === 'undefined' ? globalThis : window);
